import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, Select, DropZone, DateInput, TextArea } from '@origo/origo-ui';
import type { DocumentFormValues } from '../../../types';
import { dokumenttypeOptions } from '../../../codelists/dokumenttype';
import styles from './DocumentForm.module.css';

export type DocumentFieldErrors = Partial<Record<keyof DocumentFormValues, string>>;

interface DocumentFormProps {
    values: DocumentFormValues;
    file: File | null;
    fieldErrors?: DocumentFieldErrors;
    fileError?: string;
    onChange: (field: keyof DocumentFormValues, value: string) => void;
    onFileChange: (file: File | null) => void;
}

const DocumentForm: React.FC<DocumentFormProps> = ({
    values,
    file,
    fieldErrors = {},
    fileError,
    onChange,
    onFileChange,
}) => {
    const { t } = useTranslation();

    return (
        <div className={styles.form}>
            <TextInput
                label={t('documents.fields.tittel')}
                value={values.tittel}
                error={fieldErrors.tittel}
                onChange={e => onChange('tittel', e.target.value)}
            />
            <Select
                label={t('documents.fields.dokumenttype')}
                placeholder={t('documents.fields.dokumenttypePlaceholder')}
                value={values.dokumenttype}
                error={fieldErrors.dokumenttype}
                onChange={e => onChange('dokumenttype', e.target.value)}
                options={dokumenttypeOptions(t)}
            />
            <DateInput
                label={t('documents.fields.dato')}
                value={values.dato}
                error={fieldErrors.dato}
                onChange={e => onChange('dato', e.target.value)}
            />
            <DropZone
                label={t('documents.fields.fil')}
                accept=".pdf,.doc,.docx"
                multiple={false}
                files={file ? [{ name: file.name }] : []}
                onFiles={files => onFileChange(files[0] ?? null)}
                onRemove={() => onFileChange(null)}
                title={t('documents.fields.filTitle')}
                hint={t('documents.fields.filHint')}
            />
            {fileError && <p className={styles.fileError}>{fileError}</p>}
            <div className={styles.divider} aria-hidden />
            <TextArea
                label={t('documents.fields.beskrivelse')}
                placeholder={t('documents.fields.beskrivelsePlaceholder')}
                value={values.beskrivelse}
                error={fieldErrors.beskrivelse}
                onChange={e => onChange('beskrivelse', e.target.value)}
                rows={4}
            />
        </div>
    );
};

export default DocumentForm;
